import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../../context/LanguageContext';
import { useLocalizedProducts } from '../../hooks/useLocalizedCMS';

export default function ProductsPreview({ onEnquire }) {
  const { t } = useLanguage();
  const { products } = useLocalizedProducts();
  return (
    <section data-testid="products-preview" className="py-16 lg:py-24" style={{ backgroundColor: '#F9F8F4' }}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: '#D97706' }}>{t.products.sectionBadge}</span>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl mt-2" style={{ color: '#0A3F22' }}>
            {t.products.title} <span style={{ color: '#D97706' }}>{t.products.title2}</span>
          </h2>
          <p className="mt-3 text-base max-w-xl mx-auto" style={{ color: '#4A554A' }}>{t.products.subtitle}</p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {products.map((product, i) => (
            <motion.div
              key={product.id}
              data-testid={`product-card-${product.id}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
              className="bg-white rounded-xl overflow-hidden shadow-sm card-hover border"
              style={{ borderColor: '#E5E7EB' }}
            >
              <div className="h-44 relative overflow-hidden" style={{ backgroundColor: '#0A3F22' }}>
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.1) 60%)' }} />
                <div className="absolute bottom-3 left-4">
                  <span className="text-white font-heading font-bold text-base leading-tight">{product.name}</span>
                  <div className="text-xs mt-0.5" style={{ color: '#FCD34D' }}>{product.tagline}</div>
                </div>
              </div>

              <div className="p-4">
                <p className="text-xs leading-relaxed mb-3" style={{ color: '#4A554A' }}>{product.description.substring(0, 90)}...</p>
                <ul className="space-y-1 mb-4">
                  {product.specs.slice(0, 2).map((spec) => (
                    <li key={spec} className="flex items-center gap-1.5 text-xs" style={{ color: '#4A554A' }}>
                      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#D97706' }} />
                      {spec}
                    </li>
                  ))}
                </ul>
                <div className="flex gap-2">
                  <Link
                    to={`/products#${product.id}`}
                    data-testid={`product-details-${product.id}`}
                    className="flex-1 py-2 text-xs font-semibold text-center border rounded-lg transition-colors duration-200 hover:bg-gray-50 font-body"
                    style={{ borderColor: '#0A3F22', color: '#0A3F22' }}
                  >
                    {t.products.learnMore}
                  </Link>
                  <button
                    data-testid={`product-enquire-btn-${product.id}`}
                    onClick={() => onEnquire(product)}
                    className="flex-1 py-2 text-xs font-semibold text-white rounded-lg transition-opacity hover:opacity-90 font-body"
                    style={{ backgroundColor: '#0A3F22' }}
                  >
                    {t.products.enquire}
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-10">
          <Link
            to="/products"
            data-testid="view-all-products-btn"
            className="inline-block px-8 py-3 border-2 font-heading font-semibold rounded-lg transition-all duration-200 hover:text-white"
            style={{ borderColor: '#0A3F22', color: '#0A3F22' }}
            onMouseEnter={(e) => { e.target.style.backgroundColor = '#0A3F22'; e.target.style.color = 'white'; }}
            onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.color = '#0A3F22'; }}
          >
            View All Products →
          </Link>
        </div>
      </div>
    </section>
  );
}
